import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Trash2,
  Edit2,
  Upload,
  Download,
  Search,
  Filter,
  Workflow,
  Bell,
  Shield,
  FileText,
  SplitSquareVertical,
  Merge,
  CheckCircle2,
  Star,
  UserPlus,
  Send,
} from "lucide-react";
import toast from "react-hot-toast";
import { usePermissions } from "../../rbac/PermissionsContext";
import { helpdeskService } from "../../services/api";
import { DsButton, DsIcon, DsAlert } from "../../design-system";
import { flattenHelpdeskConfigToTwoLevels } from "../../lib/helpdeskCatalogFlatten";
import HelpdeskCatalogPanel from "./helpdesk/HelpdeskCatalogPanel";
import { ADMIN_SHELL_NAV_EVENT } from "../../utils/adminShellNav";

// Server is the source of truth; localStorage is only a resilience fallback.
const STORAGE_KEY = "adminHelpdeskConfigV2";
const TICKETS_KEY = "helpdeskTicketsV2";

const defaultConfig = {
  version: 4,
  catalogLevels: 2,
  masters: {
    ticketTypes: [
      { id: "hr", name: "HR", enabled: true },
      { id: "it", name: "IT", enabled: true },
      { id: "admin", name: "Admin", enabled: true },
      { id: "finance", name: "Finance", enabled: true },
    ],
    categories: [
      { id: "access", typeId: "it", name: "Access / Permissions", enabled: true },
      { id: "device", typeId: "it", name: "Device / Hardware", enabled: true },
      { id: "policy", typeId: "hr", name: "Policy clarification", enabled: true },
      { id: "leave", typeId: "hr", name: "Leave / Attendance", enabled: true },
    ],
    priorities: [
      {
        id: "low",
        name: "Low",
        enabled: true,
        firstResponseMinutes: 240,
        resolutionMinutes: 2880,
        autoUpgradeAfterMinutes: null,
      },
      {
        id: "medium",
        name: "Medium",
        enabled: true,
        firstResponseMinutes: 120,
        resolutionMinutes: 1440,
        autoUpgradeAfterMinutes: null,
      },
      {
        id: "high",
        name: "High",
        enabled: true,
        firstResponseMinutes: 60,
        resolutionMinutes: 720,
        autoUpgradeAfterMinutes: null,
      },
      {
        id: "critical",
        name: "Critical",
        enabled: true,
        firstResponseMinutes: 15,
        resolutionMinutes: 240,
        autoUpgradeAfterMinutes: null,
      },
    ],
    statuses: [
      { id: "open", name: "Open", pauseSla: false, enabled: true },
      { id: "in_progress", name: "In Progress", pauseSla: false, enabled: true },
      { id: "on_hold", name: "On Hold", pauseSla: true, enabled: true, holdReasonMandatory: true },
      { id: "escalated", name: "Escalated", pauseSla: false, enabled: true },
      { id: "resolved", name: "Resolved", pauseSla: false, enabled: true },
      { id: "closed", name: "Closed", pauseSla: false, enabled: true },
      { id: "reopened", name: "Reopened", pauseSla: false, enabled: true },
    ],
  },
  sla: {
    timerStartsOn: "created_at",
    pause: {
      weeklyOff: true,
      companyHoliday: true,
      assigneeLeave: true,
      onHoldStatus: true,
      onHoldReasonMandatory: true,
    },
    businessHours: {
      workingDays: [1, 2, 3, 4, 5],
      shifts: [{ id: "general", name: "General", start: "09:30", end: "18:30" }],
    },
    holidays: { dates: [], behavior: "pause_sla" },
    escalationMatrix: [
      { level: 1, breach: "first_response", notifyRoles: ["helpdesk_l1"] },
      { level: 2, breach: "resolution", notifyRoles: ["helpdesk_l2"] },
      { level: 3, breach: "resolution", notifyRoles: ["helpdesk_l3"] },
    ],
  },
  assignment: {
    autoAssignmentEnabled: true,
    mode: "category_mapping", // category_mapping | dept_mapping | role_mapping | manual_queue
    mappings: [
      // { typeId, categoryId, departmentId?, routeTo: { kind: "role"|"user"|"department", key } }
      { typeId: "it", categoryId: "it_support", routeTo: { kind: "role", key: "it_support" } },
      { typeId: "hr", categoryId: "hr_query", routeTo: { kind: "role", key: "hr_admin" } },
    ],
    allowManualReassign: true,
    allowTransfer: { role: true, user: true, department: true },
  },
  workflow: {
    makerCheckerEnabled: false,
    approvalBasedTicketsEnabled: false,
    roleRules: [
      { role: "employee", canCreate: true, canComment: true, canClose: false },
      { role: "helpdesk_l1", canCreate: true, canComment: true, canClose: true },
    ],
    transitions: [
      { from: "open", to: ["in_progress", "on_hold", "escalated"] },
      { from: "in_progress", to: ["on_hold", "escalated", "resolved"] },
      { from: "on_hold", to: ["in_progress", "escalated"] },
      { from: "resolved", to: ["closed", "reopened"] },
      { from: "reopened", to: ["in_progress", "on_hold", "escalated"] },
    ],
  },
  notifications: {
    emailEnabled: false,
    systemEnabled: true,
    triggers: {
      created: true,
      assigned: true,
      reassigned: true,
      breached: true,
      escalated: true,
      resolved: true,
      closed: true,
      reopened: true,
    },
  },
  rca: {
    mandatoryForClosure: true,
    minChars: 50,
    categories: ["Process Gap", "System Issue", "Human Error", "External Dependency"],
    templates: [
      {
        id: "default",
        name: "Default RCA",
        enabled: true,
        sections: ["What happened?", "Why happened?", "Immediate fix", "Preventive action"],
      },
    ],
    closureCompliance: {
      requireResolutionNotes: true,
      requireRca: true,
      requireSupportingDocs: false,
    },
  },
  feedback: {
    enabled: true,
    promptAfterClose: true,
    allowCommentOnly: false,
  },
  advanced: {
    allowMerge: true,
    allowSplit: true,
    knowledgeBaseEnabled: false,
    bulkUploadExcelEnabled: false,
    externalApiEnabled: false,
    auditTrailEnabled: true,
    dataRetentionDays: 365,
    prioritizationRulesEnabled: true,
  },
  /**
   * Per-level overrides. Each entry:
   * { id, scope: "type"|"category"|"subcategory", typeId?, categoryId?, subcategoryId?,
   *   sla?, workflow?, rca?, priorities?, assignment?, notifications? }
   */
  levelPolicies: [],
};

function safeParseJSON(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function loadConfig() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return defaultConfig;
  const parsed = safeParseJSON(raw, null);
  if (!parsed || typeof parsed !== "object") return defaultConfig;
  return normaliseConfig(parsed);
}

/** Guarantees every section the UI reads exists, whatever the server sends back. */
function normaliseConfig(incoming) {
  const cfg = { ...defaultConfig, ...(incoming || {}) };
  cfg.masters = { ...defaultConfig.masters, ...(incoming?.masters || {}) };
  cfg.sla = { ...defaultConfig.sla, ...(incoming?.sla || {}) };
  cfg.sla.pause = { ...defaultConfig.sla.pause, ...(incoming?.sla?.pause || {}) };
  cfg.sla.businessHours = {
    ...defaultConfig.sla.businessHours,
    ...(incoming?.sla?.businessHours || {}),
  };
  cfg.assignment = { ...defaultConfig.assignment, ...(incoming?.assignment || {}) };
  cfg.workflow = { ...defaultConfig.workflow, ...(incoming?.workflow || {}) };
  cfg.notifications = {
    ...defaultConfig.notifications,
    ...(incoming?.notifications || {}),
  };
  cfg.notifications.triggers = {
    ...defaultConfig.notifications.triggers,
    ...(incoming?.notifications?.triggers || {}),
  };
  cfg.rca = { ...defaultConfig.rca, ...(incoming?.rca || {}) };
  cfg.rca.closureCompliance = {
    ...defaultConfig.rca.closureCompliance,
    ...(incoming?.rca?.closureCompliance || {}),
  };
  cfg.feedback = { ...defaultConfig.feedback, ...(incoming?.feedback || {}) };
  cfg.advanced = { ...defaultConfig.advanced, ...(incoming?.advanced || {}) };
  cfg.levelPolicies = Array.isArray(incoming?.levelPolicies) ? incoming.levelPolicies : [];
  cfg.catalogLevels = 2;
  const { config: flat } = flattenHelpdeskConfigToTwoLevels(cfg);
  return flat;
}

function policyScopeLabel(cfg, policy) {
  const typeName =
    cfg.masters.ticketTypes.find((t) => t.id === policy.typeId)?.name || policy.typeId;
  const category = cfg.masters.categories.find((c) => c.id === policy.categoryId);
  const subName =
    (category?.subcategories || []).find((s) => s.id === policy.subcategoryId)?.name ||
    policy.subcategoryId;
  if (policy.scope === "type") return typeName || "—";
  if (policy.scope === "category") return `${typeName || "—"} › ${category?.name || policy.categoryId || "—"}`;
  if (policy.scope === "subcategory") {
    return `${typeName || "—"} › ${category?.name || policy.categoryId || "—"} › ${subName || "—"}`;
  }
  return "Global";
}

function saveConfig(cfg) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    /* ignore */
  }
}

function loadTickets() {
  const raw = localStorage.getItem(TICKETS_KEY);
  const parsed = safeParseJSON(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

function saveTickets(rows) {
  try {
    localStorage.setItem(TICKETS_KEY, JSON.stringify(rows));
  } catch {
    /* ignore */
  }
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dayKey(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function nextTicketNumber(existing) {
  const today = dayKey();
  const prefix = `HD-${today.replaceAll("-", "")}-`;
  const nums = existing
    .map((t) => String(t.ticketNo || ""))
    .filter((x) => x.startsWith(prefix))
    .map((x) => Number(x.slice(prefix.length)))
    .filter((n) => Number.isFinite(n));
  const next = (nums.length ? Math.max(...nums) : 0) + 1;
  return `${prefix}${String(next).padStart(4, "0")}`;
}

function badge(text, tone = "slate") {
  const toneMap = {
    slate: "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]",
    blue: "bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] border-[hsl(var(--ds-border))]",
    green: "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-900 border-amber-300",
    red: "bg-rose-100 text-rose-800 border-rose-300",
    purple:
      "border border-[hsl(var(--ds-secondary)/0.3)] bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] dark:border-[hsl(var(--ds-secondary)/0.5)] dark:bg-[hsl(var(--ds-secondary)/0.35)] dark:text-[hsl(var(--ds-accent-fg))]",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border ${toneMap[tone] || toneMap.slate}`}
    >
      {text}
    </span>
  );
}

function SectionCard({ title, icon: Icon, children, right }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
      <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon ? <Icon className="h-4 w-4 shrink-0 text-[hsl(var(--ds-muted-fg))]" /> : null}
          <h3 className="ds-h4 truncate text-[hsl(var(--ds-fg))]">{title}</h3>
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ModalShell({ open, title, onClose, children, wide, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/50 p-4">
      <div
        className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] ${
          wide ? "max-w-4xl" : "max-w-xl"
        }`}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div className="ds-h4 text-[hsl(var(--ds-fg))]">{title}</div>
          <DsButton type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </DsButton>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const inputSm =
  "w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 py-1.5 text-sm text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";
const fieldLabel =
  "mb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]";

function StarRating({ value = 0, onChange, readOnly = false, size = "md" }) {
  const px = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <div className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(n)}
          className={readOnly ? "cursor-default" : "cursor-pointer"}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star
            className={`${px} ${
              n <= Number(value)
                ? "fill-amber-400 text-amber-500"
                : "text-[hsl(var(--ds-muted-fg))]"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function HelpdeskConfigModal({ variant = "page" }) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  // Payroll-like framework: left sub-sidebar + main view
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const tab = new URLSearchParams(window.location.search).get("tab");
      if (["dashboard", "tickets", "catalog", "reports"].includes(tab)) return tab;
    } catch {
      /* ignore */
    }
    return "dashboard";
  });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (["dashboard", "tickets", "catalog", "reports"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    const onShellNav = (event) => {
      if (event?.detail?.module !== "helpdesk") return;
      const tab = event?.detail?.tab;
      if (["dashboard", "tickets", "catalog", "reports"].includes(tab)) {
        setActiveTab(tab);
      }
    };
    window.addEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
    return () => window.removeEventListener(ADMIN_SHELL_NAV_EVENT, onShellNav);
  }, []);
  const [config, setConfig] = useState(() => loadConfig());
  const configRef = useRef(config);
  const [tickets, setTickets] = useState(() => loadTickets());
  const [savedFlash, setSavedFlash] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketDraft, setTicketDraft] = useState({
    title: "",
    description: "",
    typeId: "it",
    categoryId: "access",
    priorityId: "medium",
    attachments: [],
  });
  const fileInputRef = useRef(null);
  const [offlineMode, setOfflineMode] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [agents, setAgents] = useState([]);
  const [detailTicketId, setDetailTicketId] = useState(null);
  const [detailDraft, setDetailDraft] = useState({
    comment: "",
    reminderNote: "",
    assigneeUserId: "",
    assigneeRoleKey: "",
    delegateUserId: "",
    delegateNote: "",
    statusReason: "",
    rca: "",
    rcaCategory: "",
    resolutionNotes: "",
    feedbackRating: 0,
    feedbackComment: "",
  });
  const [feedbackReport, setFeedbackReport] = useState(null);

  const detailTicket = useMemo(
    () => tickets.find((t) => String(t.id) === String(detailTicketId)) || null,
    [tickets, detailTicketId]
  );

  // localStorage stays in sync as a read-only fallback for API outages.
  useEffect(() => saveConfig(config), [config]);
  useEffect(() => saveTickets(tickets), [tickets]);

  useEffect(() => {
    let cancelled = false;
    helpdeskService
      .listAgents()
      .then((res) => {
        if (!cancelled) setAgents(res?.data?.agents || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshTickets = useCallback(async () => {
    try {
      const res = await helpdeskService.listTickets({});
      if (Array.isArray(res?.data)) {
        setTickets(res.data);
        setOfflineMode(false);
      }
    } catch (err) {
      setOfflineMode(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await helpdeskService.getConfig();
        if (cancelled) return;
        if (res?.data) {
          setConfig(normaliseConfig(res.data));
          configRef.current = normaliseConfig(res.data);
        }
        setOfflineMode(false);
      } catch (err) {
        if (!cancelled) setOfflineMode(true);
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
      if (!cancelled) await refreshTickets();
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTickets]);

  const loadFeedbackReport = useCallback(async () => {
    try {
      const res = await helpdeskService.getFeedbackReport();
      if (res?.data) setFeedbackReport(res.data);
    } catch {
      setFeedbackReport(null);
    }
  }, []);

  const counts = useMemo(() => {
    const byStatus = new Map();
    for (const t of tickets) byStatus.set(t.status, (byStatus.get(t.status) || 0) + 1);
    return {
      open: (byStatus.get("open") || 0) + (byStatus.get("in_progress") || 0) + (byStatus.get("on_hold") || 0) + (byStatus.get("escalated") || 0) + (byStatus.get("reopened") || 0),
      closed: (byStatus.get("closed") || 0) + (byStatus.get("resolved") || 0),
      breached: tickets.filter((t) => t.slaJson?.breached || t.sla?.breached).length,
      total: tickets.length,
    };
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((t) => {
      return (
        String(t.ticketNo || "").toLowerCase().includes(q) ||
        String(t.title || "").toLowerCase().includes(q) ||
        String(t.description || "").toLowerCase().includes(q)
      );
    });
  }, [tickets, searchTerm]);

  const tabs = useMemo(
    () => [
      { id: "dashboard", label: "Dashboard" },
      { id: "tickets", label: "Tickets" },
      { id: "catalog", label: "Catalog" },
      { id: "reports", label: "Reports" },
    ],
    []
  );

  const enabledTypes = config.masters.ticketTypes.filter((t) => t.enabled);
  const enabledCategories = config.masters.categories.filter((c) => c.enabled);
  const enabledPriorities = config.masters.priorities.filter((p) => p.enabled);
  const canViewHelpdesk =
    isAdmin ||
    hasModuleAccess("helpdesk", "view") ||
    hasScreenAccess("helpdesk", "tickets", "view") ||
    hasScreenAccess("helpdesk", "helpdesk_config", "view");
  const canViewTickets =
    isAdmin || hasScreenAccess("helpdesk", "tickets", "view");
  const canViewHelpdeskConfig =
    isAdmin || hasScreenAccess("helpdesk", "helpdesk_config", "view");
  const canCreateTickets =
    isAdmin ||
    hasModuleAccess("helpdesk", "create") ||
    hasScreenAccess("helpdesk", "tickets", "create");
  const canEditTickets =
    isAdmin ||
    hasModuleAccess("helpdesk", "edit") ||
    hasScreenAccess("helpdesk", "tickets", "edit");
  const canDeleteTickets =
    isAdmin ||
    hasModuleAccess("helpdesk", "delete") ||
    hasScreenAccess("helpdesk", "tickets", "delete");
  const canEditHelpdeskConfig =
    isAdmin ||
    hasModuleAccess("helpdesk", "edit") ||
    hasScreenAccess("helpdesk", "helpdesk_config", "edit");
  // Assignment / reminder / feedback / level-policy screens are additive: roles that
  // already carry the broader ticket or config rights keep working unchanged.
  const canManageTicketAssignment =
    canEditTickets || hasScreenAccess("helpdesk", "ticket_actions", "edit");
  const canViewTicketAssignment =
    canManageTicketAssignment || hasScreenAccess("helpdesk", "ticket_actions", "view");
  const canSubmitTicketFeedback =
    isAdmin ||
    canEditTickets ||
    hasScreenAccess("helpdesk", "ticket_feedback", "create");
  const canViewTicketFeedback =
    canSubmitTicketFeedback || hasScreenAccess("helpdesk", "ticket_feedback", "view");
  const canEditLevelPolicies =
    canEditHelpdeskConfig || hasScreenAccess("helpdesk", "helpdesk_level_policies", "edit");

  const safeSetConfig = useCallback(
    (updater) => {
      if (!canEditHelpdeskConfig) {
        toast.error("You have view-only access to Helpdesk configuration.");
        return;
      }
      setConfig(updater);
    },
    [canEditHelpdeskConfig]
  );

  const setLevelPolicyConfig = useCallback(
    (updater) => {
      if (!canEditLevelPolicies) {
        toast.error("You have view-only access to level policy overrides.");
        return;
      }
      setConfig(updater);
    },
    [canEditLevelPolicies]
  );

  const configurationTabs = useMemo(
    () => ["catalog", "reports"],
    []
  );
  const visibleTabs = useMemo(
    () =>
      tabs.filter((t) => {
        if (!canViewHelpdesk) return false;
        if (t.id === "tickets") return canViewTickets;
        if (t.id === "dashboard")
          return canViewTickets || canViewHelpdeskConfig;
        return canViewHelpdeskConfig;
      }),
    [tabs, canViewHelpdesk, canViewTickets, canViewHelpdeskConfig]
  );
  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || "dashboard");
    }
  }, [activeTab, visibleTabs]);
  const isReadOnlyConfigTab =
    configurationTabs.includes(activeTab) && !canEditHelpdeskConfig;

  useEffect(() => {
    // Defense-in-depth: if a view-only user somehow triggers any config mutation
    // handler (DOM/script bypass), immediately roll back to the last authorized
    // snapshot so permissions are still enforced at runtime.
    if (canEditHelpdeskConfig) {
      configRef.current = config;
      return;
    }
    const prev = JSON.stringify(configRef.current);
    const curr = JSON.stringify(config);
    if (prev !== curr) {
      setConfig(configRef.current);
      toast.error("You have view-only access to Helpdesk configuration.");
    }
  }, [canEditHelpdeskConfig, config]);

  const resetTicketDraft = useCallback(() => {
    const firstType = enabledTypes[0]?.id || "it";
    const firstCat = enabledCategories.find((c) => c.typeId === firstType);
    setTicketDraft({
      title: "",
      description: "",
      typeId: firstType,
      categoryId: firstCat?.id || "",
      priorityId: enabledPriorities[0]?.id || "medium",
      attachments: [],
    });
  }, [enabledTypes, enabledCategories, enabledPriorities]);

  const createTicket = async () => {
    if (!canCreateTickets) {
      toast.error("You do not have permission to create tickets.");
      return;
    }
    if (!ticketDraft.title.trim()) {
      toast.error("Ticket title is required.");
      return;
    }
    setBusy(true);
    try {
      const res = await helpdeskService.createTicket({
        title: ticketDraft.title.trim(),
        description: ticketDraft.description.trim(),
        typeId: ticketDraft.typeId,
        categoryId: ticketDraft.categoryId || null,
        priorityId: ticketDraft.priorityId,
        attachments: ticketDraft.attachments || [],
        source: "helpdesk",
      });
      if (res?.data) {
        setTickets((prev) => [res.data, ...prev]);
        setOfflineMode(false);
        window.dispatchEvent(new CustomEvent("admin:helpdesk-ticket-changed"));
        toast.success(
          `${res.data.ticketNo} created — pending with ${res.data.pendingWith || "Helpdesk Queue"}`
        );
      }
      setShowCreateTicket(false);
      resetTicketDraft();
    } catch (err) {
      // Keep the ticket locally so the user does not lose their input.
      const now = new Date().toISOString();
      const row = {
        id: `local_${Date.now()}`,
        ticketNo: nextTicketNumber(tickets),
        title: ticketDraft.title.trim(),
        description: ticketDraft.description.trim(),
        typeId: ticketDraft.typeId,
        categoryId: ticketDraft.categoryId,
        subcategoryId: null,
        priorityId: ticketDraft.priorityId,
        status: "open",
        createdAt: now,
        updatedAt: now,
        pendingWith: "Helpdesk Queue",
        commentsJson: [],
        attachmentsJson: ticketDraft.attachments || [],
        assignmentJson: { mode: config.assignment.mode, autoAssigned: false },
        slaJson: { breached: false },
        auditJson: [{ at: now, action: "created_offline" }],
        _pendingSync: true,
      };
      setTickets((prev) => [row, ...prev]);
      setOfflineMode(true);
      setShowCreateTicket(false);
      resetTicketDraft();
      toast.error(
        err?.response?.data?.message ||
          "Helpdesk service unreachable — ticket saved locally and not yet synced."
      );
    } finally {
      setBusy(false);
    }
  };

  const applyTicketResponse = useCallback((data) => {
    if (!data) return;
    setTickets((prev) => prev.map((t) => (String(t.id) === String(data.id) ? data : t)));
    // Keeps the sidebar Request/Approvals desk counts in sync.
    window.dispatchEvent(new CustomEvent("admin:helpdesk-ticket-changed"));
  }, []);

  /** Runs a ticket mutation, applies the fresh row, and surfaces API errors. */
  const runTicketAction = useCallback(
    async (fn, successMessage) => {
      setBusy(true);
      try {
        const res = await fn();
        applyTicketResponse(res?.data);
        if (successMessage) toast.success(successMessage);
        return res;
      } catch (err) {
        toast.error(err?.response?.data?.message || "Helpdesk action failed");
        return null;
      } finally {
        setBusy(false);
      }
    },
    [applyTicketResponse]
  );

  const saveFlash = useCallback(() => {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1500);
  }, []);

  // Debounced persistence of the whole config document once it has been loaded.
  const skipFirstSaveRef = useRef(true);
  useEffect(() => {
    if (!configLoaded || !(canEditHelpdeskConfig || canEditLevelPolicies)) return undefined;
    if (skipFirstSaveRef.current) {
      skipFirstSaveRef.current = false;
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        await helpdeskService.saveConfig(config);
        setOfflineMode(false);
        saveFlash();
      } catch (err) {
        setOfflineMode(true);
      }
    }, 900);
    return () => window.clearTimeout(timer);
  }, [config, configLoaded, canEditHelpdeskConfig, canEditLevelPolicies, saveFlash]);

  useEffect(() => {
    if (activeTab === "reports" || activeTab === "dashboard") loadFeedbackReport();
  }, [activeTab, loadFeedbackReport]);

  const openTicketDetail = useCallback((ticket) => {
    setDetailTicketId(ticket.id);
    setDetailDraft({
      comment: "",
      reminderNote: "",
      assigneeUserId: "",
      assigneeRoleKey: ticket.assigneeRoleKey || "",
      delegateUserId: "",
      delegateNote: "",
      statusReason: "",
      rca: typeof ticket.rcaJson?.summary === "string" ? ticket.rcaJson.summary : "",
      rcaCategory: ticket.rcaJson?.category || "",
      resolutionNotes: ticket.resolutionNotes || "",
      feedbackRating: ticket.feedbackJson?.rating || 0,
      feedbackComment: ticket.feedbackJson?.comment || "",
    });
  }, []);

  const changeTicketStatus = (ticket, nextStatus) =>
    runTicketAction(
      () =>
        helpdeskService.updateTicket(ticket.id, {
          status: nextStatus,
          holdReason: detailDraft.statusReason || undefined,
        }),
      `Ticket moved to ${nextStatus.replace(/_/g, " ")}`
    );

  const submitTicketComment = async (ticket) => {
    if (!detailDraft.comment.trim()) return;
    const res = await runTicketAction(
      () => helpdeskService.addComment(ticket.id, detailDraft.comment.trim()),
      "Comment added"
    );
    if (res) setDetailDraft((d) => ({ ...d, comment: "" }));
  };

  const assignTicket = async (ticket) => {
    const payload = detailDraft.assigneeUserId
      ? { assigneeUserId: Number(detailDraft.assigneeUserId) }
      : { assigneeRoleKey: detailDraft.assigneeRoleKey.trim() };
    if (!payload.assigneeUserId && !payload.assigneeRoleKey) {
      toast.error("Enter an assignee user ID or a role key.");
      return;
    }
    await runTicketAction(() => helpdeskService.assignTicket(ticket.id, payload), "Ticket assigned");
  };

  const delegateTicket = async (ticket) => {
    if (!detailDraft.delegateUserId) {
      toast.error("Select the user to delegate this ticket to.");
      return;
    }
    const res = await runTicketAction(
      () =>
        helpdeskService.delegateTicket(ticket.id, {
          toUserId: Number(detailDraft.delegateUserId),
          note: detailDraft.delegateNote || undefined,
        }),
      "Ticket delegated"
    );
    if (res) setDetailDraft((d) => ({ ...d, delegateUserId: "", delegateNote: "" }));
  };

  const remindTicket = async (ticket) => {
    const res = await runTicketAction(
      () => helpdeskService.remindTicket(ticket.id, detailDraft.reminderNote),
      `Reminder sent to ${ticket.pendingWith || "the current assignee"}`
    );
    if (res) setDetailDraft((d) => ({ ...d, reminderNote: "" }));
  };

  const closeTicket = async (ticket) => {
    await runTicketAction(
      () =>
        helpdeskService.closeTicket(ticket.id, {
          rca: detailDraft.rca ? { summary: detailDraft.rca } : undefined,
          rcaCategory: detailDraft.rcaCategory || undefined,
          resolutionNotes: detailDraft.resolutionNotes || undefined,
        }),
      "Ticket closed"
    );
  };

  const submitTicketFeedback = async (ticket) => {
    if (!detailDraft.feedbackRating) {
      toast.error("Select a rating between 1 and 5.");
      return;
    }
    const res = await runTicketAction(
      () =>
        helpdeskService.submitFeedback(ticket.id, {
          rating: Number(detailDraft.feedbackRating),
          comment: detailDraft.feedbackComment || undefined,
        }),
      "Thanks — your feedback was recorded"
    );
    if (res) loadFeedbackReport();
  };

  const deleteTicket = async (ticket) => {
    if (!canDeleteTickets) {
      toast.error("You do not have permission to delete tickets.");
      return;
    }
    if (!window.confirm("Delete this ticket?")) return;
    try {
      await helpdeskService.deleteTicket(ticket.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed on the server");
      return;
    }
    setTickets((prev) => prev.filter((x) => x.id !== ticket.id));
    if (String(detailTicketId) === String(ticket.id)) setDetailTicketId(null);
  };

  return (
    <div className={variant === "page" ? "ds-page" : "h-full w-full font-sans antialiased"}>
      <div className={variant === "page" ? "ds-page-shell" : "contents"}>
      <div className="ds-page-body flex h-full min-h-0 w-full flex-1 overflow-hidden">
        {/* Sub Sidebar — same shell as Employee.jsx */}
        <aside
          className="ds-page-aside"
        >
          <div className="ds-page-aside-head">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">
              Helpdesk
            </h4>
          </div>
          <div className="ds-page-aside-nav">
            {visibleTabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                className={
                    activeTab === t.id
                      ? "ds-page-nav-item ds-page-nav-item-active"
                      : "ds-page-nav-item"
                }
                  onClick={() => setActiveTab(t.id)}
                >
                {t.label}
                </button>
            ))}
          </div>

        </aside>

        {/* Tab content — flex sibling; aside width reserved in layout */}
        <div
          className="ds-page-main"
          style={isReadOnlyConfigTab ? { pointerEvents: "none" } : undefined}
        >
          <div className="ds-page-content flex min-h-0 flex-1 flex-col overflow-y-auto text-[hsl(var(--ds-fg))]">
          {isReadOnlyConfigTab && (
            <div className="ds-page-alert mb-3">
              <DsAlert tone="warning">
              View-only access: Helpdesk configuration changes are disabled for your role.
              </DsAlert>
            </div>
          )}
          {offlineMode && canViewHelpdesk && (
            <div className="ds-page-alert mb-3">
              <DsAlert tone="warning">
              Helpdesk service unavailable — showing locally cached data. Changes will not
              be saved to the server until the connection is restored.
              </DsAlert>
            </div>
          )}
          {!canViewHelpdesk && (
            <div className="flex h-full items-center justify-center p-6">
              <DsAlert tone="danger">
                You do not have access to Helpdesk for this role.
              </DsAlert>
            </div>
          )}
          {/* Dashboard */}
          {activeTab === "dashboard" && canViewHelpdesk && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Helpdesk</h1>
                  <p className="mt-1 ds-body text-[hsl(var(--ds-muted-fg))]">
                    Ticket volume, SLA risk, and quick create.
                  </p>
                </div>
                <DsButton
                  type="button"
                  disabled={!canCreateTickets}
                  onClick={() => {
                    if (!canCreateTickets) {
                      toast.error("You do not have permission to create tickets.");
                      return;
                    }
                    setShowCreateTicket(true);
                  }}
                >
                  <DsIcon icon={Plus} size="sm" />
                  Create ticket
                </DsButton>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                {[
                  { label: "Open", value: counts.open },
                  { label: "Closed / resolved", value: counts.closed },
                  { label: "SLA breaches", value: counts.breached, danger: true },
                  { label: "Total", value: counts.total },
                ].map((k) => (
                  <div
                    key={k.label}
                    className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]"
                  >
                    <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">{k.label}</div>
                    <div
                      className={`mt-1 text-2xl font-bold ${
                        k.danger
                          ? "text-[hsl(var(--ds-danger))]"
                          : "text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {k.value}
                </div>
                </div>
                ))}
              </div>

              <SectionCard title="Quick insights">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {[
                    {
                      label: "Average resolution time",
                      hint: "Shown when backend computes durations",
                    },
                    {
                      label: "Tickets by category",
                      hint: "Pivot-ready report block",
                    },
                    {
                      label: "Role-wise workload",
                      hint: "Use assignment + audit trail",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3"
                    >
                      <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                        {item.label}
                  </div>
                      <div className="mt-1 text-lg font-semibold text-[hsl(var(--ds-fg))]">
                        —
                  </div>
                      <div className="mt-1 ds-caption text-[hsl(var(--ds-muted-fg))]">
                        {item.hint}
                  </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* Tickets */}
          {activeTab === "tickets" && canViewTickets && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Tickets</h1>
                  <p className="mt-1 ds-body text-[hsl(var(--ds-muted-fg))]">
                    Search, open, assign, and close helpdesk tickets.
                  </p>
                </div>
                <DsButton
                    type="button"
                  disabled={!canCreateTickets}
                    onClick={() => {
                      if (!canCreateTickets) {
                        toast.error("You do not have permission to create tickets.");
                        return;
                      }
                      setShowCreateTicket(true);
                    }}
                >
                  <DsIcon icon={Plus} size="sm" />
                  New ticket
                </DsButton>
              </div>

              <div className="ds-page-toolbar flex flex-wrap items-center gap-2 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
                <div className="relative min-w-[240px] flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by ticket no / title / description"
                    className="h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-2 pl-9 pr-3 text-sm text-[hsl(var(--ds-fg))] outline-none placeholder:text-[hsl(var(--ds-muted-fg))] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  />
                </div>
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => toast("Filters coming soon.", { duration: 2500 })}
                >
                  <DsIcon icon={Filter} size="sm" /> Filters
                </DsButton>
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    toast("Use Reports for feedback analytics. Row export is coming soon.", {
                      duration: 3500,
                    });
                  }}
                >
                  <DsIcon icon={Download} size="sm" /> Export
                </DsButton>
              </div>

              <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
                <div className="grid grid-cols-12 gap-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  <div className="col-span-2">Ticket</div>
                  <div className="col-span-4">Title</div>
                  <div className="col-span-2">Type / Category</div>
                  <div className="col-span-1">Priority</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                <div className="max-h-[calc(100dvh-20rem)] overflow-y-auto">
                  {filteredTickets.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">No tickets found.</div>
                  ) : (
                    filteredTickets.map((t) => {
                      const typeName = config.masters.ticketTypes.find((x) => x.id === t.typeId)?.name || t.typeId;
                      const catName = config.masters.categories.find((x) => x.id === t.categoryId)?.name || t.categoryId;
                      const pr = config.masters.priorities.find((x) => x.id === t.priorityId)?.name || t.priorityId;
                      return (
                        <div
                          key={t.id}
                          className="grid grid-cols-12 items-center gap-2 border-b border-[hsl(var(--ds-border))] px-3 py-2 text-sm dark:border-[hsl(var(--ds-border))]"
                        >
                          <div className="col-span-2">
                            <button
                              type="button"
                              onClick={() => openTicketDetail(t)}
                              className="text-left font-semibold text-[hsl(var(--ds-primary))] underline-offset-2 hover:underline"
                            >
                              {t.ticketNo}
                            </button>
                            <div className="text-[11px] text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">{new Date(t.createdAt).toLocaleString()}</div>
                          </div>
                          <div className="col-span-4 min-w-0">
                            <button
                              type="button"
                              onClick={() => openTicketDetail(t)}
                              className="block w-full truncate text-left font-medium text-[hsl(var(--ds-fg))] dark:text-[hsl(var(--ds-fg))]"
                            >
                              {t.title}
                            </button>
                            <div className="truncate text-[11px] text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">
                              {t.pendingWith ? `Pending with ${t.pendingWith}` : t.description || "—"}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs dark:text-[hsl(var(--ds-muted-fg))]">{typeName}</div>
                            <div className="text-[11px] text-[hsl(var(--ds-muted-fg))] dark:text-[hsl(var(--ds-muted-fg))]">{catName}</div>
                          </div>
                          <div className="col-span-1">
                            {badge(pr, pr === "Critical" ? "red" : pr === "High" ? "amber" : "slate")}
                          </div>
                          <div className="col-span-1">{badge(t.status, t.status === "closed" ? "green" : "blue")}</div>
                          <div className="col-span-2 flex justify-end gap-1">
                            {config.advanced.allowMerge && (
                              <button
                                type="button"
                                className="border border-[hsl(var(--ds-border))] p-2 hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:hover:bg-[hsl(var(--ds-muted))]"
                                title="Merge"
                              >
                                <Merge className="h-4 w-4" />
                              </button>
                            )}
                            {config.advanced.allowSplit && (
                              <button
                                type="button"
                                className="border border-[hsl(var(--ds-border))] p-2 hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:hover:bg-[hsl(var(--ds-muted))]"
                                title="Split"
                              >
                                <SplitSquareVertical className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="border border-[hsl(var(--ds-border))] p-2 hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:hover:bg-[hsl(var(--ds-muted))]"
                              title="Open ticket"
                              onClick={() => openTicketDetail(t)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={!canDeleteTickets}
                              className={`border border-[hsl(var(--ds-border))] p-2 text-rose-700 hover:bg-[hsl(var(--ds-muted))] dark:border-[hsl(var(--ds-border))] dark:text-rose-400 dark:hover:bg-[hsl(var(--ds-muted))] ${!canDeleteTickets ? "cursor-not-allowed opacity-45" : ""}`}
                              title="Delete"
                              onClick={() => deleteTicket(t)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "catalog" && canViewHelpdeskConfig && (
            <HelpdeskCatalogPanel
                  config={config}
              safeSetConfig={safeSetConfig}
              setLevelPolicyConfig={setLevelPolicyConfig}
              canEdit={canEditHelpdeskConfig}
              canEditLevelPolicies={canEditLevelPolicies}
              saveFlash={saveFlash}
            />
          )}

          {activeTab === "reports" && canViewHelpdeskConfig && (
            <div className="space-y-4">
                      <div>
                <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Reports</h1>
                <p className="mt-1 ds-body text-[hsl(var(--ds-muted-fg))]">
                  Volume, SLA risk, and requester feedback on closed tickets.
                </p>
                      </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                {[
                  { label: "Open", value: counts.open },
                  { label: "Closed / resolved", value: counts.closed },
                  { label: "SLA breaches", value: counts.breached },
                  { label: "Total", value: counts.total },
                ].map((k) => (
                  <div
                    key={k.label}
                    className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 shadow-[var(--ds-shadow-card)]"
                  >
                    <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">{k.label}</div>
                    <div className="mt-1 text-2xl font-bold text-[hsl(var(--ds-fg))]">{k.value}</div>
                    </div>
                  ))}
                </div>

              {canViewTicketFeedback ? (
                <SectionCard title="Requester feedback">
                {!feedbackReport ? (
                    <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
                      No feedback data yet. Closed tickets with ratings will appear here.
                    </p>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                        <div>
                          <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">Responses</div>
                          <div className="text-xl font-semibold">{feedbackReport.responses}</div>
                        </div>
                        <div>
                          <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">Avg rating</div>
                          <div className="text-xl font-semibold">{feedbackReport.avgRating || "—"}</div>
                      </div>
                    <div>
                          <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">Response rate</div>
                          <div className="text-xl font-semibold">{feedbackReport.responseRate}%</div>
                      </div>
                    <div>
                          <div className="ds-caption text-[hsl(var(--ds-muted-fg))]">Closed tickets</div>
                          <div className="text-xl font-semibold">{feedbackReport.closedTickets}</div>
                      </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <span
                            key={n}
                            className="rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-1 ds-caption"
                          >
                            {n}★ · {feedbackReport.distribution?.[n] || 0}
                              </span>
                        ))}
                            </div>
                      {(feedbackReport.recent || []).length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="ds-table w-full text-left text-sm">
                            <thead>
                              <tr className="border-b border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]">
                                <th className="px-2 py-2 font-semibold">Ticket</th>
                                <th className="px-2 py-2 font-semibold">Type</th>
                                <th className="px-2 py-2 font-semibold">Rating</th>
                                <th className="px-2 py-2 font-semibold">Comment</th>
                              </tr>
                            </thead>
                            <tbody>
                              {feedbackReport.recent.map((r) => (
                                <tr
                                  key={`${r.ticketNo}-${r.submittedAt}`}
                                  className="border-b border-[hsl(var(--ds-border))] last:border-0"
                                >
                                  <td className="px-2 py-2">{r.ticketNo}</td>
                                  <td className="px-2 py-2">{r.typeName || "—"}</td>
                                  <td className="px-2 py-2">{r.rating}★</td>
                                  <td className="px-2 py-2 text-[hsl(var(--ds-muted-fg))]">
                                    {r.comment || "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                  </div>
                )}
              </SectionCard>
              ) : null}
            </div>
          )}

          {/* Save feedback */}
          <div className="mt-6 min-h-[1rem] text-xs text-emerald-700">
            {savedFlash ? "Saved." : ""}
          </div>
          </div>
        </div>
      </div>

      {/* Create ticket modal */}
      <ModalShell
        open={showCreateTicket && canCreateTickets}
        title="Create helpdesk ticket"
        onClose={() => setShowCreateTicket(false)}
        wide
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">Title *</div>
            <input
              value={ticketDraft.title}
              onChange={(e) => setTicketDraft((d) => ({ ...d, title: e.target.value }))}
              className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              placeholder="Short summary"
            />
          </div>
          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">Description</div>
            <textarea
              value={ticketDraft.description}
              onChange={(e) => setTicketDraft((d) => ({ ...d, description: e.target.value }))}
              className="w-full rounded-lg border border-[hsl(var(--ds-border))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              rows={4}
              placeholder="Details, impact, steps tried..."
            />
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">Ticket type</div>
            <select
              value={ticketDraft.typeId}
              onChange={(e) => {
                const typeId = e.target.value;
                const cat = enabledCategories.find((c) => c.typeId === typeId);
                setTicketDraft((d) => ({
                  ...d,
                  typeId,
                  categoryId: cat?.id || "",
                }));
              }}
              className="w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            >
              {enabledTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">Priority</div>
            <select
              value={ticketDraft.priorityId}
              onChange={(e) => setTicketDraft((d) => ({ ...d, priorityId: e.target.value }))}
              className="w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            >
              {enabledPriorities.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">Category</div>
            <select
              value={ticketDraft.categoryId}
              onChange={(e) => {
                const categoryId = e.target.value;
                setTicketDraft((d) => ({
                  ...d,
                  categoryId,
                }));
              }}
              className="w-full rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            >
              <option value="">Select category</option>
              {enabledCategories
                .filter((c) => c.typeId === ticketDraft.typeId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">Attachments</div>
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => {
                const files = Array.from(e.target.files || []).map((f) => ({ name: f.name, size: f.size, type: f.type }));
                setTicketDraft((d) => ({ ...d, attachments: [...(d.attachments || []), ...files] }));
                e.target.value = "";
              }} />
              <button
                type="button"
                className="flex items-center gap-2 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm font-medium text-[hsl(var(--ds-fg))] transition-colors hover:bg-[hsl(var(--ds-muted))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] dark:border-[hsl(var(--ds-border))] dark:bg-[hsl(var(--ds-surface))] dark:text-[hsl(var(--ds-fg))] dark:hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => {
                  if (!canCreateTickets) {
                    toast.error("You do not have permission to create tickets.");
                    return;
                  }
                  fileInputRef.current?.click();
                }}
              >
                <Upload className="h-4 w-4" /> Add files
              </button>
              <span className="text-xs text-[hsl(var(--ds-muted-fg))]">Stored as metadata (demo).</span>
            </div>
            {(ticketDraft.attachments || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {ticketDraft.attachments.map((a, idx) => (
                  <span key={idx} className="text-[11px] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-2 py-1">
                    {a.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] pt-3">
          <DsButton variant="outline" onClick={() => setShowCreateTicket(false)}>
            Cancel
          </DsButton>
          <DsButton onClick={createTicket} disabled={!canCreateTickets} loading={busy}>
            Create
          </DsButton>
        </div>
      </ModalShell>

      {/* Ticket detail */}
      <ModalShell
        open={!!detailTicket}
        title={
          detailTicket
            ? `${detailTicket.ticketNo} — ${detailTicket.title}`
            : "Ticket"
        }
        onClose={() => setDetailTicketId(null)}
        wide
      >
        {detailTicket ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                ["Type", detailTicket.typeName || detailTicket.typeId],
                ["Category", detailTicket.categoryName || detailTicket.categoryId],
                ["Priority", detailTicket.priorityName || detailTicket.priorityId],
                ["Priority", detailTicket.priorityName || detailTicket.priorityId],
                ["Status", detailTicket.statusName || detailTicket.status],
                ["Pending with", detailTicket.pendingWith || "—"],
                [
                  "Resolution due",
                  detailTicket.slaJson?.resolutionDue
                    ? new Date(detailTicket.slaJson.resolutionDue).toLocaleString()
                    : "—",
                ],
                ["Policy level", detailTicket.policyScope || "global"],
              ].map(([label, value]) => (
                <div key={label} className="border border-[hsl(var(--ds-border))] p-2">
                  <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                    {label}
                  </div>
                  <div className="text-sm text-[hsl(var(--ds-fg))]">{value || "—"}</div>
                </div>
              ))}
            </div>

            {detailTicket.description ? (
              <div className="border border-[hsl(var(--ds-border))] p-3 text-sm text-[hsl(var(--ds-fg))]">
                {detailTicket.description}
              </div>
            ) : null}

            {/* Status transitions */}
            {(detailTicket.nextStatuses || []).length > 0 && canEditTickets && (
              <SectionCard title="Move ticket" icon={Workflow}>
                <div className="space-y-2">
                  <input
                    value={detailDraft.statusReason}
                    onChange={(e) =>
                      setDetailDraft((d) => ({ ...d, statusReason: e.target.value }))
                    }
                    placeholder="Reason / hold note (required for On Hold)"
                    className={inputSm}
                  />
                  <div className="flex flex-wrap gap-2">
                    {detailTicket.nextStatuses.map((s) => (
                      <DsButton
                        key={s}
                        variant="outline"
                        size="sm"
                        loading={busy}
                        onClick={() => changeTicketStatus(detailTicket, s)}
                      >
                        {config.masters.statuses.find((x) => x.id === s)?.name || s}
                      </DsButton>
                    ))}
                  </div>
                </div>
              </SectionCard>
            )}

            {/* Assign & delegate */}
            {canViewTicketAssignment && (
              <SectionCard title="Assignment" icon={UserPlus}>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className={fieldLabel}>Assign to</div>
                    <select
                      value={detailDraft.assigneeUserId}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, assigneeUserId: e.target.value }))
                      }
                      disabled={!canManageTicketAssignment}
                      className={inputSm}
                    >
                      <option value="">Select user</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                    <input
                      value={detailDraft.assigneeRoleKey}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, assigneeRoleKey: e.target.value }))
                      }
                      placeholder="…or role key (e.g. hr_admin)"
                      readOnly={!canManageTicketAssignment}
                      className={inputSm}
                    />
                    {canManageTicketAssignment && (
                      <DsButton size="sm" loading={busy} onClick={() => assignTicket(detailTicket)}>
                        <DsIcon icon={UserPlus} size="sm" /> Assign
                      </DsButton>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className={fieldLabel}>Delegate to</div>
                    <select
                      value={detailDraft.delegateUserId}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, delegateUserId: e.target.value }))
                      }
                      disabled={!canManageTicketAssignment}
                      className={inputSm}
                    >
                      <option value="">Select user</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                    <input
                      value={detailDraft.delegateNote}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, delegateNote: e.target.value }))
                      }
                      placeholder="Note (optional)"
                      readOnly={!canManageTicketAssignment}
                      className={inputSm}
                    />
                    {canManageTicketAssignment && (
                      <DsButton
                        size="sm"
                        variant="secondary"
                        loading={busy}
                        onClick={() => delegateTicket(detailTicket)}
                      >
                        <DsIcon icon={Send} size="sm" /> Delegate
                      </DsButton>
                    )}
                  </div>
                </div>
                {detailTicket.delegatedFromUserId ? (
                  <div className="mt-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    Delegated from user #{detailTicket.delegatedFromUserId}
                  </div>
                ) : null}
              </SectionCard>
            )}

            {/* Remind — requester only */}
            {detailTicket.isRaiser && detailTicket.status !== "closed" && (
              <SectionCard title="Send reminder" icon={Bell}>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[240px] flex-1">
                    <div className={fieldLabel}>Note to {detailTicket.pendingWith || "assignee"}</div>
                    <input
                      value={detailDraft.reminderNote}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, reminderNote: e.target.value }))
                      }
                      placeholder="Optional message"
                      className={inputSm}
                    />
                  </div>
                  <DsButton size="sm" loading={busy} onClick={() => remindTicket(detailTicket)}>
                    <DsIcon icon={Bell} size="sm" /> Remind
                  </DsButton>
                </div>
                {(detailTicket.remindersJson || []).length > 0 && (
                  <div className="mt-3 space-y-1">
                    {detailTicket.remindersJson.map((r, idx) => (
                      <div
                        key={idx}
                        className="border border-[hsl(var(--ds-border))] px-2 py-1 text-[11px] text-[hsl(var(--ds-muted-fg))]"
                      >
                        {new Date(r.at).toLocaleString()} — {r.by} → {r.sentTo || "assignee"}
                        {r.note ? `: ${r.note}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            )}

            {/* Close with RCA */}
            {canEditTickets && detailTicket.status !== "closed" && (
              <SectionCard title="Close ticket" icon={CheckCircle2}>
                <div className="space-y-2">
                  <div>
                    <div className={fieldLabel}>Resolution notes</div>
                    <textarea
                      rows={2}
                      value={detailDraft.resolutionNotes}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, resolutionNotes: e.target.value }))
                      }
                      className={inputSm}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <div className={fieldLabel}>
                        RCA {detailTicket.rcaRequiredForClosure ? "(mandatory)" : "(optional)"}
                      </div>
                      <textarea
                        rows={3}
                        value={detailDraft.rca}
                        onChange={(e) => setDetailDraft((d) => ({ ...d, rca: e.target.value }))}
                        placeholder="What happened, why, immediate fix, preventive action"
                        className={inputSm}
                      />
                    </div>
                    <div>
                      <div className={fieldLabel}>RCA category</div>
                      <select
                        value={detailDraft.rcaCategory}
                        onChange={(e) =>
                          setDetailDraft((d) => ({ ...d, rcaCategory: e.target.value }))
                        }
                        className={inputSm}
                      >
                        <option value="">Select</option>
                        {config.rca.categories.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <DsButton size="sm" loading={busy} onClick={() => closeTicket(detailTicket)}>
                    <DsIcon icon={CheckCircle2} size="sm" /> Close ticket
                  </DsButton>
                </div>
              </SectionCard>
            )}

            {/* Feedback after closure — requester only */}
            {detailTicket.status === "closed" && canViewTicketFeedback && (
              <SectionCard title="Your feedback" icon={Star}>
                {detailTicket.feedbackJson ? (
                  <div className="space-y-1">
                    <StarRating value={detailTicket.feedbackJson.rating} readOnly />
                    <div className="text-sm text-[hsl(var(--ds-fg))]">
                      {detailTicket.feedbackJson.comment || "No comment"}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                      Submitted {new Date(detailTicket.feedbackJson.submittedAt).toLocaleString()}
                    </div>
                  </div>
                ) : detailTicket.isRaiser && canSubmitTicketFeedback ? (
                  <div className="space-y-2">
                    <StarRating
                      value={detailDraft.feedbackRating}
                      onChange={(n) => setDetailDraft((d) => ({ ...d, feedbackRating: n }))}
                    />
                    <textarea
                      rows={2}
                      value={detailDraft.feedbackComment}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, feedbackComment: e.target.value }))
                      }
                      placeholder="How was the resolution?"
                      className={inputSm}
                    />
                    <DsButton
                      size="sm"
                      loading={busy}
                      onClick={() => submitTicketFeedback(detailTicket)}
                    >
                      <DsIcon icon={Star} size="sm" /> Submit feedback
                    </DsButton>
                  </div>
                ) : (
                  <div className="text-sm text-[hsl(var(--ds-muted-fg))]">
                    Feedback is pending from the employee who raised this ticket.
                  </div>
                )}
              </SectionCard>
            )}

            {/* Conversation */}
            <SectionCard title="Comments" icon={FileText}>
              <div className="space-y-2">
                {(detailTicket.commentsJson || []).length === 0 ? (
                  <div className="text-sm text-[hsl(var(--ds-muted-fg))]">No comments yet.</div>
                ) : (
                  detailTicket.commentsJson.map((c, idx) => (
                    <div key={idx} className="border border-[hsl(var(--ds-border))] p-2">
                      <div className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        {c.by} · {new Date(c.at).toLocaleString()}
                      </div>
                      <div className="text-sm text-[hsl(var(--ds-fg))]">{c.text}</div>
                    </div>
                  ))
                )}
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[240px] flex-1">
                    <input
                      value={detailDraft.comment}
                      onChange={(e) =>
                        setDetailDraft((d) => ({ ...d, comment: e.target.value }))
                      }
                      placeholder="Add a comment"
                      className={inputSm}
                    />
                  </div>
                  <DsButton
                    size="sm"
                    variant="outline"
                    loading={busy}
                    onClick={() => submitTicketComment(detailTicket)}
                  >
                    Comment
                  </DsButton>
                </div>
              </div>
            </SectionCard>

            {/* Audit */}
            {(detailTicket.auditJson || []).length > 0 && (
              <SectionCard title="Audit trail" icon={Shield}>
                <div className="space-y-1">
                  {detailTicket.auditJson.map((a, idx) => (
                    <div
                      key={idx}
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-[11px] text-[hsl(var(--ds-muted-fg))]"
                    >
                      {new Date(a.at).toLocaleString()} — {a.action}
                      {a.by ? ` by ${a.by}` : ""}
                      {a.to ? ` → ${a.to}` : ""}
                      {a.reason ? ` (${a.reason})` : ""}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}
          </div>
        ) : null}
      </ModalShell>
      </div>
    </div>
  );
}

